import BusinessLogoUpload from '../../BusinessLogoUpload';
import type { LeadFormConfig } from '../../../lib/crm/leadForms';
import type { LeadFormColorTarget } from './LeadFormColorPickerModal';

type Props = {
  config: LeadFormConfig;
  onChange: (patch: Partial<LeadFormConfig>) => void;
  onPickColor: (target: LeadFormColorTarget, current: string) => void;
};

const shell = {
  fontFamily:
    '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
};

function ToggleRow({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center justify-between gap-3 py-2.5 touch-manipulation">
      <div className="min-w-0">
        <p className="text-[14px] font-medium text-gray-900">{label}</p>
        {description ? <p className="text-[12px] text-gray-500 mt-0.5">{description}</p> : null}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-7 w-12 flex-shrink-0 rounded-full border-2 border-transparent transition-colors ${
          checked ? 'bg-blue-500' : 'bg-gray-300'
        }`}
      >
        <span
          className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow transition ${
            checked ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
    </label>
  );
}

function ColorRow({
  label,
  color,
  onClick,
}: {
  label: string;
  color: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center justify-between gap-3 py-2.5 text-left touch-manipulation active:bg-gray-50 rounded-[10px] px-1"
      style={{ WebkitTapHighlightColor: 'transparent' }}
    >
      <span className="text-[14px] font-medium text-gray-900">{label}</span>
      <span
        className="h-8 w-8 rounded-full border border-gray-200 shadow-sm"
        style={{ backgroundColor: color }}
      />
    </button>
  );
}

export default function LeadFormAppearancePanel({ config, onChange, onPickColor }: Props) {
  return (
    <div className="space-y-5" style={shell}>
      <div>
        <label className="block text-[12px] font-medium text-gray-500 mb-1.5">Subtitle</label>
        <input
          type="text"
          value={config.sub_header || ''}
          onChange={(e) => onChange({ sub_header: e.target.value })}
          placeholder="Optional subtitle under the title"
          className="w-full px-3 py-2.5 border border-gray-200 rounded-[10px] text-[15px] focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          style={{ minHeight: 44 }}
        />
      </div>

      <div>
        <p className="text-[12px] font-semibold text-gray-400 uppercase tracking-[0.04em] mb-1">
          Colors
        </p>
        <div className="divide-y divide-gray-100">
          <ColorRow
            label="Header"
            color={config.header_color || '#000000'}
            onClick={() => onPickColor('header', config.header_color || '#000000')}
          />
          <ColorRow
            label="Background"
            color={config.background_color || '#ffffff'}
            onClick={() => onPickColor('background', config.background_color || '#ffffff')}
          />
          <ColorRow
            label="Text"
            color={config.text_color || '#111827'}
            onClick={() => onPickColor('text', config.text_color || '#111827')}
          />
          <ColorRow
            label="Button"
            color={config.button_color || '#2563eb'}
            onClick={() => onPickColor('button', config.button_color || '#2563eb')}
          />
        </div>
      </div>

      <div>
        <p className="text-[12px] font-semibold text-gray-400 uppercase tracking-[0.04em] mb-2">
          Logo
        </p>
        <BusinessLogoUpload />
        <p className="text-[12px] text-gray-500 mt-2">
          Uses your business logo across lead forms and the rest of the platform.
        </p>
      </div>

      <div>
        <p className="text-[12px] font-semibold text-gray-400 uppercase tracking-[0.04em] mb-1">
          Header display
        </p>
        <div className="divide-y divide-gray-100">
          <ToggleRow
            label="Show header"
            checked={config.show_header !== false}
            onChange={(v) => onChange({ show_header: v })}
          />
          <ToggleRow
            label="Business name"
            checked={config.show_business_name !== false}
            onChange={(v) => onChange({ show_business_name: v })}
          />
          <ToggleRow
            label="Phone"
            checked={Boolean(config.show_phone)}
            onChange={(v) => onChange({ show_phone: v })}
          />
          <ToggleRow
            label="Email"
            checked={Boolean(config.show_email)}
            onChange={(v) => onChange({ show_email: v })}
          />
          <ToggleRow
            label="Address"
            checked={Boolean(config.show_address)}
            onChange={(v) => onChange({ show_address: v })}
          />
          <ToggleRow
            label="Direct contact block"
            description="Show phone / email / address together"
            checked={Boolean(config.show_direct_contact)}
            onChange={(v) => onChange({ show_direct_contact: v })}
          />
        </div>
      </div>
    </div>
  );
}
